import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { VentasService } from 'src/app/services/ventas.service';
import { PagosService } from 'src/app/services/pagos.service';
import { GenericFormHandlerService } from 'src/app/shared/generic-crud.service';
import { controlHttp, VarType } from 'src/app/shared/models/form.models';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { VentasDialogMobileComponent } from './ventas-dialog-mobile/ventas-dialog-mobile.component';
import { finalize } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MetodoPago, METODO_PAGO_LABELS } from 'src/app/enums/metodo-pago.enum';

@Component({
  selector: 'app-ventas-dialog',
  templateUrl: './ventas-dialog.component.html',
  styleUrls: ['./ventas-dialog.component.scss']
})
export class VentasDialogComponent extends GenericFormHandlerService implements OnInit {
  ventaDetalles: any;
  private isHandset = false;
  isRegisteringVenta = false;
  pagos: any[] = [];
  mostrarFormularioAbono = false;
  abonoForm: FormGroup;
  totalPagado = 0;
  saldoPendiente = 0;
  @Input() ventaId: number;
  MetodoPago = MetodoPago;
  metodosPago = Object.values(MetodoPago);
  metodoPagoLabels = METODO_PAGO_LABELS;

  constructor(
    protected ventaService: VentasService,
    protected toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<VentasDialogComponent>,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private pagosService: PagosService,
    private fb: FormBuilder
  ) {
    super(ventaService, toastr, dialogRef);
    this.ventaId = this.data?.venta?.id;
    this.configureBasedOnData();
    this.breakpointObserver.observe([Breakpoints.Handset, '(max-width: 750px)']).subscribe(result => {
      this.isHandset = result.matches;
    });
    this.abonoForm = this.fb.group({
      monto: [0, [Validators.required, Validators.min(0.01)]],
      metodoPago: [MetodoPago.EFECTIVO, Validators.required]
    });
  }

  ngOnInit(): void {
    if (this.isHandset) {
      this.dialog.open(VentasDialogMobileComponent, {
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        panelClass: 'ventas-dialog-mobile-panel',
        autoFocus: false,
        data: this.data
      });
      this.dialogRef.close();
      return;
    }

    this.obtenerDetalles();
    this.initializeForm(false);
  }

  private configureBasedOnData(): void {
    this.currentOperation = controlHttp.READ;
    if (this.data.venta) {
      this.apiIdentifier = this.data.venta.id;
    }
  }

  getEstadoColor(estado: string): string {
    switch (estado) {
      case 'completada': return 'primary';
      case 'pendiente': return 'accent';
      case 'cancelada': return 'warn';
      default: return '';
    }
  }

  imprimirTicket(): void {
    this.toastr.success('Ticket enviado a impresión', 'Éxito');
    this.dialogRef?.close();
  }

  exportarPdf(): void {
    if (!this.ventaDetalles) {
      this.toastr.warning('No hay información para exportar');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const margin = 36;
    const lineHeight = 18;
    let cursorY = margin;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Ticket - Detalles de Venta', margin, cursorY);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    cursorY += lineHeight * 1.5;
    doc.text(`No. Ticket: ${this.ventaDetalles.id}`, margin, cursorY);
    doc.text(`Fecha: ${new Date(this.ventaDetalles.createdAt).toLocaleString()}`, margin + 220, cursorY);

    cursorY += lineHeight;
    doc.text(`Vendedor: ${this.ventaDetalles.usuario?.name || 'N/D'}`, margin, cursorY);
    doc.text(`Estado: ${this.ventaDetalles.estado}`, margin + 220, cursorY);

    cursorY += lineHeight * 1.5;
    doc.setFont('helvetica', 'bold');
    doc.text('Productos', margin, cursorY);

    const body = (this.ventaDetalles.detalles || []).map((detalle: any, index: number) => {
      const producto = detalle.producto || {};
      return [
        index + 1,
        producto.nombre || 'N/D',
        producto.proveedorNombre || 'Sin proveedor',
        detalle.cantidad,
        Number(detalle.precioUnitario || 0).toFixed(2),
        Number(detalle.subtotal || 0).toFixed(2)
      ];
    });

    autoTable(doc, {
      startY: cursorY + lineHeight / 2,
      head: [['#', 'Producto', 'Proveedor', 'Cant.', 'P. Unitario', 'Subtotal']],
      body,
      theme: 'grid',
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 251] },
      columnStyles: {
        0: { halign: 'center', cellWidth: 32 },
        1: { cellWidth: 150 },
        2: { cellWidth: 120 },
        3: { halign: 'center', cellWidth: 45 },
        4: { halign: 'right', cellWidth: 70 },
        5: { halign: 'right', cellWidth: 70 }
      },
      styles: { font: 'helvetica', fontSize: 10 }
    });

    const finalY = (doc as any).lastAutoTable.finalY || cursorY + lineHeight;
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen', margin, finalY + lineHeight);

    doc.setFont('helvetica', 'normal');
    const resumenY = finalY + lineHeight * 1.5;
    doc.text(`Subtotal: ${Number(this.ventaDetalles.total - this.ventaDetalles.impuesto || 0).toFixed(2)}`, margin, resumenY);
    doc.text(`Impuesto: ${Number(this.ventaDetalles.impuesto || 0).toFixed(2)}`, margin + 200, resumenY);
    doc.text(`Total: ${Number(this.ventaDetalles.total || 0).toFixed(2)}`, margin + 350, resumenY);

    const pagoY = resumenY + lineHeight;
    doc.text(`Recibido: ${Number(this.ventaDetalles.dineroRecibido || 0).toFixed(2)}`, margin, pagoY);
    doc.text(`Cambio: ${Number(this.ventaDetalles.cambio || 0).toFixed(2)}`, margin + 200, pagoY);

    const footerY = doc.internal.pageSize.getHeight() - margin;
    doc.setFontSize(10);
    doc.text('¡Gracias por su compra!', margin, footerY - lineHeight);
    doc.text('Para devoluciones, presente este ticket dentro de los 15 días', margin, footerY);

    doc.save(`ticket-venta-${this.ventaDetalles.id}.pdf`);
    this.toastr.success('PDF generado correctamente', 'Exportación');
  }

  registrarVenta(ventaData?: any): void {
    if (this.isRegisteringVenta) {
      return;
    }

    const payload = ventaData ?? this.ventaDetalles;

    if (!payload || !payload.detalles || payload.detalles.length === 0) {
      this.toastr.warning('No hay información de venta para registrar');
      return;
    }

    this.isRegisteringVenta = true;

    this.ventaService.crearVentaConDetalles(payload)
      .pipe(finalize(() => (this.isRegisteringVenta = false)))
      .subscribe({
        next: () => {
          this.toastr.success('Venta registrada exitosamente');
          this.dialogRef.close(true);
        },
        error: (error) => {
          const errorMessage = this.extractErrorMessage(error);
          this.toastr.error(errorMessage || 'Error al registrar la venta');
        }
      });
  }

  onCancel(): void {
    this.dialogRef?.close();
  }

  obtenerDetalles(): void {
    this.isLoading = true;
    this.ventaService.get(`${this.data?.venta?.id}/detalles`).subscribe({
      next: (venta) => {
        this.ventaDetalles = venta;
        this.cargarPagos();
        this.isLoading = false;
      },
      error: (error) => {
        const errorMessage = this.extractErrorMessage(error);
        this.toastr.error(errorMessage || 'Error al cargar los datos');
        this.isLoading = false;
      }
    });
  }

  cargarPagos(): void {
    if (!this.ventaDetalles?.id) return;

    this.pagosService.obtenerPagosPorVenta(this.ventaDetalles.id).subscribe({
      next: (pagos) => {
        this.pagos = pagos || [];
        this.calcularTotales();
      },
      error: () => {
        this.pagos = [];
        this.calcularTotales();
      }
    });
  }

  calcularTotales(): void {
    this.totalPagado = this.pagos.reduce((sum, pago) => sum + (pago.monto || 0), 0);
    if (this.ventaDetalles) {
      this.saldoPendiente = this.ventaDetalles.total - this.totalPagado;
    }
  }

  toggleFormularioAbono(): void {
    this.mostrarFormularioAbono = !this.mostrarFormularioAbono;
    if (this.mostrarFormularioAbono) {
      this.abonoForm.patchValue({ monto: Math.max(0, this.saldoPendiente) });
    }
  }

  registrarAbono(): void {
    if (this.abonoForm.invalid) {
      this.toastr.warning('Ingrese un monto válido');
      return;
    }

    const monto = this.abonoForm.get('monto')?.value;
    if (monto <= 0) {
      this.toastr.warning('El monto debe ser mayor a cero');
      return;
    }

    if (monto > this.saldoPendiente) {
      this.toastr.warning(`El monto no puede ser mayor al saldo pendiente (${this.saldoPendiente})`);
      return;
    }

    this.isLoading = true;
    const metodoPago = this.abonoForm.get('metodoPago')?.value;
    var dtoAbono = {
      monto: monto,
      metodoPago: metodoPago
    }
    this.pagosService.post(dtoAbono, `venta/${this.ventaDetalles.id}/abono`).subscribe({
      next: () => {
        this.toastr.success('Abono registrado exitosamente');
        this.abonoForm.reset();
        this.mostrarFormularioAbono = false;
        this.cargarPagos();
        // Recargar detalles de venta para actualizar el estado
        this.obtenerDetalles();
      },
      error: (error) => {
        const errorMessage = this.extractErrorMessage(error);
        this.toastr.error(errorMessage || 'Error al registrar el abono');
        this.isLoading = false;
      }
    });
  }

  puedeAgregarAbono(): boolean {
    return this.ventaDetalles?.estado === 'pendiente' && this.saldoPendiente > 0;
  }
}

interface DialogData {
  venta?: any;
  isEdit?: boolean;
}
